import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Pause, Play, Building2, MapPin, ShieldAlert, Wifi, AlertTriangle, CreditCard, Upload, Bell, BarChart3, Battery } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AdminTabShell } from '@/components/admin/AdminTabShell';
import { OrgLookupTab } from '@/components/admin/OrgLookupTab';
import { PropertyLookupTab } from '@/components/admin/PropertyLookupTab';
import { BillingDiagnosticsTab } from '@/components/admin/BillingDiagnosticsTab';
import { OnboardingDiagnosticsTab } from '@/components/admin/OnboardingDiagnosticsTab';
import { GatewayDiagnosticsTab } from '@/components/admin/GatewayDiagnosticsTab';
import { NotificationDiagnosticsTab } from '@/components/admin/NotificationDiagnosticsTab';
import { DataQualityTab } from '@/components/admin/DataQualityTab';
import { BatteryAnomaliesTab } from '@/components/admin/BatteryAnomaliesTab';
import {
  useAdminSupportOverview,
  type OfflineGateway,
  type StripeConnectIncomplete,
  type FailedImportJob,
  type NotificationFailure,
} from '@/hooks/admin/useAdminSupportOverview';
import { useAdminDataQuality } from '@/hooks/admin/useAdminDataQuality';
import { TIER_LABELS, TIER_BADGE_CLASSES } from '@/lib/data-quality-labels';

const REFRESH_INTERVAL = 60_000;

function StatCard({ title, value, icon: Icon, variant }: {
  title: string;
  value: number | string;
  icon: React.ElementType;
  variant?: 'default' | 'warning' | 'error';
}) {
  const colors = {
    default: 'text-wt-text-muted',
    warning: 'text-wt-amber-primary',
    error: 'text-wt-red-primary',
  };
  const color = colors[variant ?? 'default'];
  return (
    <Card className="border-wt-border bg-wt-bg-secondary">
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-wt-text-muted">{title}</p>
            <p className={`text-2xl font-semibold font-mono ${color}`}>{value}</p>
          </div>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
      </CardContent>
    </Card>
  );
}

function ReadinessBreakdown({ data }: { data: Record<string, number> }) {
  const labels: Record<string, string> = {
    ready: 'Ready',
    provisional: 'Provisional',
    warming_up: 'Warming Up',
    connecting: 'Connecting',
    learning: 'Learning',
    degraded: 'Degraded',
  };
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    ready: 'default',
    provisional: 'outline',
    warming_up: 'secondary',
    connecting: 'secondary',
    learning: 'secondary',
    degraded: 'destructive',
  };
  const entries = Object.entries(data).sort(([a], [b]) => {
    const order = ['ready', 'provisional', 'warming_up', 'connecting', 'learning', 'degraded'];
    return order.indexOf(a) - order.indexOf(b);
  });

  return (
    <Card className="border-wt-border bg-wt-bg-secondary">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-xs text-wt-text-muted font-normal">Properties by Readiness</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="flex flex-wrap gap-2">
          {entries.map(([state, count]) => (
            <Badge key={state} variant={variants[state] ?? 'secondary'} className="gap-1 text-xs">
              {labels[state] ?? state}: {count}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function OfflineGatewaysTable({ rows }: { rows: OfflineGateway[] }) {
  if (rows.length === 0) return <p className="text-xs text-wt-text-muted py-2">None</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-wt-text-muted border-b border-wt-border">
          <th className="text-left py-1.5 pr-3 font-medium">Claim Code</th>
          <th className="text-left py-1.5 pr-3 font-medium">Property</th>
          <th className="text-left py-1.5 pr-3 font-medium">Org</th>
          <th className="text-left py-1.5 pr-3 font-medium">Status</th>
          <th className="text-left py-1.5 font-medium">Last Heartbeat</th>
        </tr></thead>
        <tbody>
          {rows.map(gw => (
            <tr key={gw.id} className="border-b border-wt-border/50">
              <td className="py-1.5 pr-3 font-mono">{gw.claim_code}</td>
              <td className="py-1.5 pr-3">{gw.property_name ?? '—'}</td>
              <td className="py-1.5 pr-3">{gw.org_name ?? '—'}</td>
              <td className="py-1.5 pr-3">
                <Badge variant="destructive" className="text-[10px]">{gw.operational_status}</Badge>
              </td>
              <td className="py-1.5 text-wt-text-muted">
                {gw.last_heartbeat_at ? new Date(gw.last_heartbeat_at).toLocaleString() : 'Never'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StripeConnectTable({ rows }: { rows: StripeConnectIncomplete[] }) {
  if (rows.length === 0) return <p className="text-xs text-wt-text-muted py-2">None</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-wt-text-muted border-b border-wt-border">
          <th className="text-left py-1.5 pr-3 font-medium">Org</th>
          <th className="text-left py-1.5 pr-3 font-medium">Charges</th>
          <th className="text-left py-1.5 font-medium">Payouts</th>
        </tr></thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.id} className="border-b border-wt-border/50">
              <td className="py-1.5 pr-3">{row.name}</td>
              <td className="py-1.5 pr-3">
                <Badge variant={row.charges_enabled ? 'default' : 'destructive'} className="text-[10px]">
                  {row.charges_enabled ? 'Yes' : 'No'}
                </Badge>
              </td>
              <td className="py-1.5">
                <Badge variant={row.payouts_enabled ? 'default' : 'destructive'} className="text-[10px]">
                  {row.payouts_enabled ? 'Yes' : 'No'}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FailedImportsTable({ rows }: { rows: FailedImportJob[] }) {
  if (rows.length === 0) return <p className="text-xs text-wt-text-muted py-2">None in last 30 days</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-wt-text-muted border-b border-wt-border">
          <th className="text-left py-1.5 pr-3 font-medium">Org</th>
          <th className="text-left py-1.5 pr-3 font-medium">Provider</th>
          <th className="text-left py-1.5 pr-3 font-medium">Plants</th>
          <th className="text-left py-1.5 font-medium">Created</th>
        </tr></thead>
        <tbody>
          {rows.map(job => (
            <tr key={job.id} className="border-b border-wt-border/50">
              <td className="py-1.5 pr-3">{job.org_name ?? '—'}</td>
              <td className="py-1.5 pr-3 font-mono">{job.provider_type}</td>
              <td className="py-1.5 pr-3">{job.processed_plants}/{job.total_plants}</td>
              <td className="py-1.5 text-wt-text-muted">{new Date(job.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotificationFailuresTable({ rows }: { rows: NotificationFailure[] }) {
  if (rows.length === 0) return <p className="text-xs text-wt-text-muted py-2">None in last 7 days</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-wt-text-muted border-b border-wt-border">
          <th className="text-left py-1.5 pr-3 font-medium">Property</th>
          <th className="text-left py-1.5 pr-3 font-medium">Title</th>
          <th className="text-left py-1.5 pr-3 font-medium">Error</th>
          <th className="text-left py-1.5 font-medium">When</th>
        </tr></thead>
        <tbody>
          {rows.map(n => (
            <tr key={n.id} className="border-b border-wt-border/50">
              <td className="py-1.5 pr-3">{n.property_name ?? '—'}</td>
              <td className="py-1.5 pr-3">{n.title}</td>
              <td className="py-1.5 pr-3 text-wt-red-primary max-w-[200px] truncate">
                {n.email_error ?? n.sms_error ?? '—'}
              </td>
              <td className="py-1.5 text-wt-text-muted">{new Date(n.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SupportDashboard() {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(Date.now());
  const [secondsAgo, setSecondsAgo] = useState(0);

  const interval = autoRefresh ? REFRESH_INTERVAL : false;
  const overview = useAdminSupportOverview(true, interval);
  const dataQuality = useAdminDataQuality(true, interval);

  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastRefreshedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [lastRefreshedAt]);

  useEffect(() => {
    if (overview.dataUpdatedAt && overview.dataUpdatedAt > 0) {
      setLastRefreshedAt(overview.dataUpdatedAt);
    }
  }, [overview.dataUpdatedAt]);

  const handleRefresh = useCallback(() => {
    overview.refetch();
    dataQuality.refetch();
  }, [overview, dataQuality]);

  const formatSecondsAgo = (s: number) => {
    if (s < 60) return `${s}s ago`;
    return `${Math.floor(s / 60)}m ago`;
  };

  const d = overview.data;

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-wt-text-primary tracking-tight">
            SUPPORT DASHBOARD
          </h1>
          <p className="text-sm text-wt-text-muted font-mono">
            Last refreshed: {formatSecondsAgo(secondsAgo)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setAutoRefresh(v => !v)} className="gap-1.5 text-xs">
            {autoRefresh ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={overview.isFetching} className="gap-1.5 text-xs">
            <RefreshCw className={`h-3 w-3 ${overview.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-wt-bg-secondary border border-wt-border">
          <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
          <TabsTrigger value="org-lookup" className="text-xs">Org Lookup</TabsTrigger>
          <TabsTrigger value="property-lookup" className="text-xs">Property Lookup</TabsTrigger>
          <TabsTrigger value="billing" className="text-xs">Billing</TabsTrigger>
          <TabsTrigger value="onboarding" className="text-xs">Onboarding</TabsTrigger>
          <TabsTrigger value="gateway" className="text-xs">Gateway</TabsTrigger>
          <TabsTrigger value="notifications" className="text-xs">Notifications</TabsTrigger>
          <TabsTrigger value="data-quality" className="text-xs">Data Quality</TabsTrigger>
          <TabsTrigger value="battery-health" className="text-xs">Battery Health</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <AdminTabShell isLoading={overview.isLoading} error={overview.error}>
            {d && (
              <>
                {/* Stat Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  <StatCard title="Organizations" value={d.total_orgs} icon={Building2} />
                  <StatCard title="Properties" value={d.total_properties} icon={MapPin} />
                  <StatCard title="Active Subscriptions" value={d.active_service_subscriptions} icon={CreditCard} />
                  <StatCard
                    title="Open Issues"
                    value={d.open_customer_issues}
                    icon={ShieldAlert}
                    variant={d.open_customer_issues > 0 ? 'warning' : 'default'}
                  />
                  <StatCard
                    title="Offline Gateways"
                    value={d.offline_gateways.length}
                    icon={Wifi}
                    variant={d.offline_gateways.length > 0 ? 'error' : 'default'}
                  />
                </div>

                {/* Readiness breakdown */}
                <ReadinessBreakdown data={d.properties_by_readiness_state} />

                {/* Data quality tier breakdown */}
                {dataQuality.data && (
                  <Card className="border-wt-border bg-wt-bg-secondary">
                    <CardHeader className="pb-2 pt-4 px-4">
                      <CardTitle className="text-xs text-wt-text-muted font-normal flex items-center gap-1.5">
                        <BarChart3 className="h-3.5 w-3.5" />
                        Data Quality Distribution
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <div className="flex flex-wrap gap-2">
                        {['excellent', 'good', 'watch', 'poor', 'unusable', 'unscored']
                          .filter(t => dataQuality.data!.tierDistribution[t])
                          .map(t => (
                            <span key={t} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${TIER_BADGE_CLASSES[t] ?? ''}`}>
                              {TIER_LABELS[t] ?? t}: {dataQuality.data!.tierDistribution[t]}
                            </span>
                          ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Detail tables */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                  <Card className="border-wt-border bg-wt-bg-secondary">
                    <CardHeader className="pb-2 pt-4 px-4">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Wifi className="h-4 w-4 text-wt-red-primary" />
                        Offline Gateways ({d.offline_gateways.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <OfflineGatewaysTable rows={d.offline_gateways} />
                    </CardContent>
                  </Card>

                  <Card className="border-wt-border bg-wt-bg-secondary">
                    <CardHeader className="pb-2 pt-4 px-4">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-wt-amber-primary" />
                        Stripe Connect Incomplete ({d.stripe_connect_not_onboarded.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <StripeConnectTable rows={d.stripe_connect_not_onboarded} />
                    </CardContent>
                  </Card>

                  <Card className="border-wt-border bg-wt-bg-secondary">
                    <CardHeader className="pb-2 pt-4 px-4">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Upload className="h-4 w-4 text-wt-red-primary" />
                        Failed Imports ({d.recent_failed_import_jobs.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <FailedImportsTable rows={d.recent_failed_import_jobs} />
                    </CardContent>
                  </Card>

                  <Card className="border-wt-border bg-wt-bg-secondary">
                    <CardHeader className="pb-2 pt-4 px-4">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Bell className="h-4 w-4 text-wt-red-primary" />
                        Notification Failures ({d.recent_notification_failures.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <NotificationFailuresTable rows={d.recent_notification_failures} />
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </AdminTabShell>
        </TabsContent>

        <TabsContent value="org-lookup">
          <OrgLookupTab />
        </TabsContent>

        <TabsContent value="property-lookup">
          <PropertyLookupTab />
        </TabsContent>

        <TabsContent value="billing">
          <BillingDiagnosticsTab />
        </TabsContent>

        <TabsContent value="onboarding">
          <OnboardingDiagnosticsTab />
        </TabsContent>

        <TabsContent value="gateway">
          <GatewayDiagnosticsTab />
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationDiagnosticsTab />
        </TabsContent>

        <TabsContent value="data-quality">
          <DataQualityTab
            data={dataQuality.data ?? null}
            isLoading={dataQuality.isLoading}
            error={dataQuality.error as Error | null}
          />
        </TabsContent>

        <TabsContent value="battery-health">
          <BatteryAnomaliesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
